<script setup>
import { computed } from 'vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconCloud from '@/components/icon/IconCloud.vue'
import { formatClockTime, formatDurationShort } from './mediaflowMeta'

/**
 * Small chip next to the status pill while a session runs: when the server
 * stops the production. Gold for the last stretch of the session window.
 * Renders nothing offline.
 */
const props = defineProps({
	/** From `sessionWindow()`; null when no session. */
	session: { type: Object, default: null },
	/** offline · starting · live · disabled */
	status: { type: String, default: 'offline' },
})

const running = computed(() => props.status === 'live' || props.status === 'starting')
const chip = computed(() => {
	if (!props.session || !running.value) return null
	const { endsAt, remainingSec, endingSoon } = props.session
	return { at: formatClockTime(endsAt), left: formatDurationShort(remainingSec), endingSoon, ended: remainingSec <= 0 }
})
const tooltip = computed(() => {
	if (!chip.value) return ''
	if (chip.value.ended) return 'The session reached its time limit. The server is shutting the production down.'
	return `Safe to close this tab. Stops at ${chip.value.at} (${chip.value.left} left).`
})
</script>

<template>
	<Tooltip v-if="chip" :content="tooltip" wrapper-class="inline-flex" popper-class="max-w-[260px] text-sm break-words">
		<span
			class="inline-flex h-6 shrink-0 items-center gap-1 rounded-full px-2 text-tiny font-medium tabular-nums ring-1"
			:class="chip.endingSoon || chip.ended ? 'bg-warning-transparent text-gold ring-gold' : 'bg-surface-3 text-surface-text-1 ring-surface-4'"
			role="status"
			data-testid="session-chip"
			:data-ending-soon="chip.endingSoon ? 'true' : undefined"
		>
			<icon-base width="12" height="12" :width-view-box="16" :height-view-box="16" fill="none" class="shrink-0"><icon-cloud /></icon-base>
			<template v-if="chip.ended">time is up</template>
			<template v-else>stops {{ chip.at }}</template>
		</span>
	</Tooltip>
</template>
